import { DropdownMenuItem } from "@/components/ui/baseUi/dropdown-menu"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { cn } from "@/modules/lib/utils"
import { word } from "@modules/core/utils/i18n"
import { ProgramBundleSubscriberEntity } from "@modules/programBundle/programBundleSubscribers/entities/programBundleSubscriberEntity"
import {
  ProgramSubscriberDeleteModal,
  type ProgramSubscriberDeleteModalRef,
} from "@modules/programs/programSubscriber/components/programSubscriberDeleteModal"
import { ProgramSubscriberDetailsViewTabs } from "@modules/programs/programSubscriber/entities/programSubsciberType"
import { ProgramSubscriberEntity } from "@modules/programs/programSubscriber/entities/programSubscriberEntity"
import { useProgramSubscriberActions } from "@modules/programs/programSubscriber/hooks/useProgramSubscriber"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { EllipsisMenu } from "@modules/ui/components/ellipsisMenu"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { BookTextIcon, Pause, Percent, Play, SquareMinus } from "lucide-react"
import { useRef } from "react"

type ProgramSubscriberDetailsMenuProps = {
  subscriberDetails: ProgramSubscriberEntity | ProgramBundleSubscriberEntity
  setActiveTab: (tab: ProgramSubscriberDetailsViewTabs) => void
  programId: string
  className?: string
} & React.ComponentProps<"button">

export function ProgramSubscriberDetailsMenu({
  subscriberDetails,
  setActiveTab,
  programId,
  className,
  ...props
}: ProgramSubscriberDetailsMenuProps) {
  const programSubscriberDeleteModalRef = useRef<ProgramSubscriberDeleteModalRef>(null)
  const popup = useConfirmPopup()
  const { sidebarNavigateBack } = useSidebar()

  const { removeSubscriber } = useProgramSubscriberActions(programId, subscriberDetails)

  const onRemoveSubscriberClick = () => {
    if (subscriberDetails.paidPrice && subscriberDetails.paidPrice.value > 0) {
      programSubscriberDeleteModalRef.current?.open()
    } else {
      popup.show({
        title: "",
        message: word("program.subscribers.subscriptionDetails.remove.title"),
        accept: async () => {
          await removeSubscriber(false)
            .then(() => {
              toast.success(word("program.subscribers.subscriptionDetails.remove.success"), {
                classNames: successToastClassName(),
              })
              sidebarNavigateBack()
            })
            .catch(() => {
              toast.error(word("program.subscribers.subscriptionDetails.remove.error"), {
                classNames: errorToastClassName(),
              })
            })
        },
        footerProps: {
          confirmText: word("global.delete"),
        },
      })
    }
  }

  return (
    <>
      <EllipsisMenu propsContent={{ side: "bottom" }} className={cn(className)} {...props}>
        {/* PAUSE */}
        {subscriberDetails.canSubscriptionBePaused() && (
          <DropdownMenuItem
            key="pause"
            onClick={() => {
              setActiveTab(ProgramSubscriberDetailsViewTabs.PAUSE_FORM)
            }}
          >
            {subscriberDetails.pause ? (
              <Play className="fill-textDefault-body text-textDefault-body" strokeWidth={1} size={16} />
            ) : (
              <Pause className="fill-textDefault-body text-textDefault-body" strokeWidth={1} size={16} />
            )}

            {subscriberDetails.pause
              ? word("program.subscribers.subscriptionDetails.pause.resume")
              : word("program.subscribers.subscriptionDetails.pause.suspend")}
          </DropdownMenuItem>
        )}

        {/* COUPON */}
        {subscriberDetails.canApplyCoupon() && (
          <DropdownMenuItem
            key="coupon"
            onClick={() => {
              setActiveTab(ProgramSubscriberDetailsViewTabs.DISCOUNT_FORM)
            }}
          >
            <Percent className="text-textDefault-body" strokeWidth={1} size={16} />
            {subscriberDetails.coupon
              ? word("program.subscribers.subscriptionDetails.coupon.modify")
              : word("program.subscribers.subscriptionDetails.coupon.create")}
          </DropdownMenuItem>
        )}

        {/* INTERNAL NOTE */}
        <DropdownMenuItem
          key="internalNote"
          onClick={() => {
            setActiveTab(ProgramSubscriberDetailsViewTabs.INTERNAL_NOTE_FORM)
          }}
        >
          <BookTextIcon className="text-textDefault-body" strokeWidth={1} size={16} />
          {word("program.subscribers.subscriptionDetails.internalNote.form.editTitle")}
        </DropdownMenuItem>

        {/* REMOVE SUBSCRIBER */}
        {subscriberDetails.canDeleteSubscriber() && (
          <DropdownMenuItem
            key="removeSubscriber"
            onClick={() => {
              onRemoveSubscriberClick()
            }}
          >
            <SquareMinus className="text-textError" strokeWidth={1} size={16} />
            <span className="text-textError">{word("program.subscribers.subscriptionDetails.remove.buttonLabel")}</span>
          </DropdownMenuItem>
        )}
      </EllipsisMenu>
      <ProgramSubscriberDeleteModal
        ref={programSubscriberDeleteModalRef}
        subscriberDetails={subscriberDetails}
        programId={programId}
      />
    </>
  )
}
